import { logger } from "@/utils/logger";

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { translations } from '../translations';
import { decodeHtmlEntities } from '../utils/htmlDecoder';

type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
}

// Helper function to detect language from HTML meta tag
const detectLanguageFromMeta = (): Language => {
  try {
    // Look for content-language meta tag
    const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
    if (metaTag && metaTag.content) {
      const contentLang = metaTag.content.toLowerCase();
      // Extract language code (e.g., 'fr' from 'fr-ca', 'en' from 'en-us')
      const langCode = contentLang.split('-')[0];
      if (langCode === 'fr' || langCode === 'en') {
        logger.log(`🌐 Detected language from meta tag: ${langCode} (from ${contentLang})`);
        return langCode as Language;
      }
    }
  } catch (error) {
    logger.warn('Failed to detect language from meta tag:', error);
  }
  
  logger.log('🌐 Using default language: en');
  return 'en';
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  initialLanguage 
}) => {
  // Use provided initialLanguage, or detect from meta tag, or default to 'en'
  const detectedLanguage = initialLanguage || detectLanguageFromMeta();
  const [language, setLanguageInternal] = useState<Language>(detectedLanguage);

  const setLanguage = (lang: Language) => {
    logger.log('🌐🌐🌐 LANGUAGE CONTEXT - setLanguage called with:', lang);
    logger.log('🌐🌐🌐 LANGUAGE CONTEXT - Current language:', language);
    logger.log('🌐🌐🌐 LANGUAGE CONTEXT - About to change language...');
    setLanguageInternal(lang);
    logger.log('🌐🌐🌐 LANGUAGE CONTEXT - Language change completed!');
  };

  const t = (key: string): string => {
    const rawTranslation = translations[language][key] || key;
    return decodeHtmlEntities(rawTranslation);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    console.error('useLanguage called outside of LanguageProvider. This may be due to improper component mounting or hot reload issues.');
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
